import { Spin, Table, Tag } from "antd";
import { EmptyState } from "../../../../components/EmptyState";
import type {
  GisImportBatchData,
  GisImportRowData,
  GisImportRowPage,
  GisImportRowStatus,
} from "../gisApi";

/**
 * Cột phải MÀN C — kết quả đối soát.
 *
 * Bám ĐÚNG hợp đồng: chỉ in `totalRows`/`validRows`/`quarantinedRows` và các trường
 * thật của dòng (`rowIndex`/`targetEntityCode`/`geometryType`/`status`/`quarantineReason`).
 * KHÔNG có cột "Thêm mới / Cập nhật" — `actionType` không tồn tại ở hợp đồng lẫn backend
 * (deviation F-3, Spec §11).
 *
 * Phân trang chạy ở SERVER: `page` gửi lên là 0-based (`GisImportController.java:119`)
 * còn antd `Table.current` là 1-based — quy đổi ở đúng hai chỗ dưới đây.
 */

type PageMeta = GisImportRowPage["meta"];

const STATUS_LABELS: Record<GisImportRowStatus, string> = {
  PENDING: "Chờ xử lý",
  VALID: "Hợp lệ",
  QUARANTINED: "Cách ly",
  COMMITTED: "Đã nạp",
  FAILED: "Lỗi",
};

const FILTERS: ReadonlyArray<{ value: GisImportRowStatus | "ALL"; label: string }> = [
  { value: "ALL", label: "Tất cả" },
  { value: "VALID", label: "Chỉ hợp lệ" },
  { value: "QUARANTINED", label: "Chỉ cách ly" },
];

function statusTone(row: GisImportRowData): string {
  if (row.status === "QUARANTINED" || row.status === "FAILED") {
    return row.quarantineSeverity === "WARNING" ? "warning" : "error";
  }
  if (row.status === "VALID" || row.status === "COMMITTED") return "success";
  return "default";
}

export type GisImportReviewProps = Readonly<{
  batch: GisImportBatchData;
  rows: GisImportRowData[];
  meta: PageMeta | null;
  loading: boolean;
  statusFilter: GisImportRowStatus | "ALL";
  onStatusFilterChange: (value: GisImportRowStatus | "ALL") => void;
  onPageChange: (page0: number, size: number) => void;
}>;

export function GisImportReview({
  batch,
  rows,
  meta,
  loading,
  statusFilter,
  onStatusFilterChange,
  onPageChange,
}: GisImportReviewProps): React.JSX.Element {
  return (
    <div className="gis-import-review">
      <div className="gis-review-head">
        <div className="gis-review-summary">
          <span className="gis-review-stat">{`${batch.totalRows ?? 0} dòng`}</span>
          <span className="gis-review-sep" aria-hidden="true">
            ·
          </span>
          <span className="gis-review-stat is-valid">{`${batch.validRows ?? 0} hợp lệ`}</span>
          <span className="gis-review-sep" aria-hidden="true">
            ·
          </span>
          <span className="gis-review-stat is-quarantined">{`${batch.quarantinedRows ?? 0} cách ly`}</span>
        </div>

        <div className="gis-review-filters" role="group" aria-label="Lọc theo trạng thái dòng">
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              aria-pressed={statusFilter === filter.value}
              className={
                statusFilter === filter.value
                  ? "gis-review-filter is-active"
                  : "gis-review-filter"
              }
              onClick={() => onStatusFilterChange(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="gis-review-loading">
          <Spin size="small" />
          <span>Đang tải dòng đối soát…</span>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Không có dòng nào khớp bộ lọc"
          description="Đổi bộ lọc để xem các dòng khác."
        />
      ) : (
        <Table<GisImportRowData>
          dataSource={rows}
          rowKey="rowIndex"
          size="small"
          className="gis-review-table"
          pagination={
            meta
              ? {
                  current: meta.page + 1,
                  pageSize: meta.size,
                  total: meta.totalElements,
                  showSizeChanger: false,
                  size: "small",
                }
              : false
          }
          onChange={(pagination) =>
            onPageChange((pagination.current ?? 1) - 1, pagination.pageSize ?? meta?.size ?? 20)
          }
          columns={[
            {
              title: "Hàng",
              dataIndex: "rowIndex",
              key: "rowIndex",
              width: 80,
              render: (value: number) => <span className="gis-review-rownum">{value}</span>,
            },
            {
              title: "Mã đích",
              dataIndex: "targetEntityCode",
              key: "targetEntityCode",
              render: (value?: string | null) => value ?? "—",
            },
            {
              title: "Hình học",
              dataIndex: "geometryType",
              key: "geometryType",
              width: 110,
            },
            {
              title: "Trạng thái",
              dataIndex: "status",
              key: "status",
              width: 130,
              render: (value: GisImportRowStatus, row: GisImportRowData) => (
                <Tag color={statusTone(row)}>{STATUS_LABELS[value]}</Tag>
              ),
            },
            {
              title: "Vì sao",
              dataIndex: "quarantineReason",
              key: "quarantineReason",
              render: (value?: string | null) => value ?? "—",
            },
          ]}
        />
      )}
    </div>
  );
}
